import { Inbox } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import useTranslations from "@/shared/lang/useTranslations";
import { groupByDay } from "@/shared/utils/groupByDay";

import type { ActivityRow as ActivityRowData } from "../types/paymentsActivity.types";
import { ActivityRow } from "./ActivityRow";

export interface ActivityListProps {
  rows: ActivityRowData[];
  isLoading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowClick: (row: ActivityRowData) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const SkeletonRow = () => (
  <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3.5 border-b border-border px-4 py-3 last:border-b-0 sm:grid-cols-[auto_1fr_auto_auto]">
    <div className="h-8 w-8 animate-pulse rounded-md bg-muted" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
      <div className="h-2.5 w-2/5 animate-pulse rounded bg-muted" />
    </div>
    <div className="hidden h-4 w-10 animate-pulse rounded bg-muted sm:block" />
    <div className="h-4 w-20 animate-pulse rounded bg-muted" />
  </div>
);

/**
 * The runtime activity list, grouped by calendar day like the wallet statement.
 *
 * A flat table gave every row the same weight; grouping restores the "when" a monitor is read by. Ordering is
 * the server's (`created_at DESC`), so the groups come out newest-first without a client sort.
 */
export function ActivityList({
  rows,
  isLoading,
  page,
  pageSize,
  total,
  onPageChange,
  onRowClick,
  hasActiveFilters,
  onClearFilters,
}: ActivityListProps) {
  const { t, activeLanguage } = useTranslations("paymentsActivity");

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {Array.from({ length: 8 }).map((_, index) => (
          <SkeletonRow key={index} />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <ColocaEmpty
        icon={Inbox}
        title={t("empty_title")}
        description={hasActiveFilters ? t("empty_filtered_description") : t("empty_description")}
      >
        {hasActiveFilters && (
          <ColocaButton variant="default" onClick={onClearFilters}>
            {t("filter_clear")}
          </ColocaButton>
        )}
      </ColocaEmpty>
    );
  }

  const groups = groupByDay(rows, {
    getTimestamp: (row) => row.createdAt,
    todayLabel: t("list_group_today"),
    yesterdayLabel: t("list_group_yesterday"),
    formatDate: (isoDate) => {
      // The key is a LOCAL `YYYY-MM-DD`; `new Date(isoDate)` would read it as UTC midnight and shift the
      // label a day back for negative-offset timezones, so build the date from its parts instead.
      const [year, month, day] = isoDate.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      if (Number.isNaN(date.getTime())) return isoDate;
      return new Intl.DateTimeFormat(activeLanguage, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    },
  });

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
        {groups.map((group, index) => (
          <div key={group.key}>
            <div
              className={`flex items-center justify-between border-b border-border bg-muted/40 px-4 py-2.5 ${
                index > 0 ? "border-t" : ""
              }`}
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
                {group.label || "—"}
              </span>
              <span className="font-mono text-[10.5px] text-muted-foreground">{group.items.length}</span>
            </div>
            {group.items.map((row) => (
              <ActivityRow key={row.id} row={row} onClick={onRowClick} />
            ))}
          </div>
        ))}
      </div>

      {total > pageSize && (
        <ColocaPagination
          page={page}
          perPage={pageSize}
          total={total}
          onPageChange={onPageChange}
          summary={t("pagination_summary", {
            from: (page - 1) * pageSize + 1,
            to: Math.min(page * pageSize, total),
            total,
          })}
        />
      )}
    </div>
  );
}
